
const libPictProvider = require('pict-provider');
const libUserSearch = require('../access/User-Search.js');

const libModel = require('../approval-workflow/ApprovalWorkflow-Model.js');

/**
 * Bindings families. Approval workflows are a SHARED pool (one 'ApprovalWorkflow' Configuration namespace);
 * documents and observations differ ONLY in the bindings layer — a distinct Configuration NamespaceValue on
 * 'ApprovalWorkflowBindings', a different target entity to bind, and how each target row yields its key/label.
 *   - Document:    targets = ReportNamedInstance (key = .Hash, label = .Name).
 *   - Observation: targets = ObservationManifest (key = .Name — matches Observation.ObservationType — label
 *                  = .DisplayName || .Name; there is no .Hash column).
 * NOTE: the approvals microservice currently only ENFORCES 'Document' (it early-returns null for other entity
 * types and `WorkflowsEnabledForType` lists only Document), so observation bindings written here are inert
 * until the server drops those guards. The storage + editor are already generic.
 */
// Project-scope binding-dropdown sentinels (distinct from any real workflow hash): "use the customer default"
// and "no workflow (opt out of the default)". The provider maps these to the effective-map edits the config
// service expects on save (see setBinding).
const BINDING_INHERIT = '__INHERIT__';
const BINDING_NONE = '__NONE__';

const BINDING_FAMILIES =
{
	Document:
	{
		EntityType: 'Document',
		Title: 'Document Bindings',
		TargetNoun: 'document type',
		TargetNounPlural: 'document types',
		ServerPending: false,
		targetsURL: (pIDCustomer) => `ReportNamedInstances/By/IDCustomer/${pIDCustomer}/0/2000`,
		targetKey: (pRow) => pRow && pRow.Hash,
		targetLabel: (pRow) => (pRow && (pRow.Name || pRow.Hash)) || '',
	},
	Observation:
	{
		EntityType: 'Observation',
		Title: 'Observation Bindings',
		TargetNoun: 'observation type',
		TargetNounPlural: 'observation types',
		ServerPending: true,
		// SECURITY: scope to this customer (+ global IDCustomer 0). ObservationManifest carries a per-customer
		// IDCustomer, and its server-side Read authz is `Allow` (NOT MyCustomer-scoped), so a bare `ObservationManifests`
		// fetch returns every tenant's observation-type config to anyone who opens this route. Mirror the Document
		// family's per-customer scoping + cap. (Defense-in-depth: the schema Read authz should also move to MyCustomer.)
		targetsURL: (pIDCustomer) => `ObservationManifests/FilteredTo/FBL~IDCustomer~INN~0,${pIDCustomer}~FBV~Deleted~EQ~0/0/2000`,
		targetKey: (pRow) => pRow && pRow.Name,
		targetLabel: (pRow) => (pRow && (pRow.DisplayName || pRow.Name)) || '',
	},
};

/**
 * ApprovalWorkflowProvider — the data layer behind the config-app approval-workflow editor.
 *
 * The editor edits the Configuration BLOB (NamespaceKey 'ApprovalWorkflow'), NOT the DB-table approval
 * engine. Because storage is the shared Config blob, this config-app editor is a drop-in for Home's — same
 * read/write endpoints, same blob shape, same generated sentences.
 *
 * Responsibilities:
 *   - List every workflow for the customer (Configuration/For/ApprovalWorkflow/In/Customer/:id → { hash: blob }).
 *   - Load one workflow and pre-resolve the role/user display names its sentences need.
 *   - Save one workflow (Configuration/Put/ApprovalWorkflow/:hash/Customer/:id), unchanged apart from the user's edits.
 *   - Gate create/edit/save to a customer admin (super-user OR a UserRole with PermissionLevel 'CustomerAdmin').
 *
 * State lives in pict.AppData.ApprovalWorkflowEditor; the views derive a clean render-state from it.
 */
class ApprovalWorkflowProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	// --- Session context + write-permission (mirrors ConfigViewer / the server's gate) ---

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get idCustomer()
	{
		return this.currentUser.CustomerID;
	}

	get isSuperUser()
	{
		const tmpUser = this.currentUser;
		return (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1);
	}

	get hasCustomerAdmin()
	{
		// A customer super-admin ('CustomerAdmin' or 'SuperAdminUser' — the latter is how the Access workspace
		// recognises a customer-level admin) may edit. UI gate only; the Configuration/Put server gate is
		// authoritative, so being slightly permissive here just means a rejected save surfaces as an error.
		const tmpRoles = this.currentUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && (pRole.PermissionLevel === 'CustomerAdmin' || pRole.PermissionLevel === 'SuperAdminUser'));
	}

	/** Can the current user create / edit / save workflows? UI gate only — the server is authoritative. */
	get canEdit()
	{
		return this.isSuperUser || this.hasCustomerAdmin;
	}

	/** The shared editor state slice (lazily created). */
	get state()
	{
		if (!this.pict.AppData.ApprovalWorkflowEditor)
		{
			this.pict.AppData.ApprovalWorkflowEditor =
			{
				// List page.
				List: [], ListLoading: false, ListError: '',
				// Editor page.
				Editor:
				{
					Hash: '', Workflow: null, Names: { RoleNameByLevel: {}, UserNameByID: {} },
					Findings: [], DocumentCounts: null, Loading: false, Error: '', Editable: false, IsNew: false,
				},
			};
		}
		return this.pict.AppData.ApprovalWorkflowEditor;
	}

	// --- REST helpers ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/**
	 * Configuration/Get returns EITHER { Config, LevelControlRecords } OR the raw config object depending on
	 * the namespace (mirrors ConfigViewer / PermissionMatrix). Normalize to the config object.
	 * @return {Object}
	 */
	_unwrapConfig(pResult)
	{
		if (pResult && pResult.Config !== undefined)
		{
			return pResult.Config || {};
		}
		return pResult || {};
	}

	// --- List ---

	/**
	 * Load every ApprovalWorkflow the customer has, as a list of { Hash, StateCount, TransitionCount }.
	 */
	async loadListAsync()
	{
		const tmpState = this.state;
		tmpState.ListLoading = true;
		tmpState.ListError = '';
		tmpState.List = [];
		tmpState.ProjectOnlyList = [];
		this._repaintList();
		try
		{
			const tmpMap = await this._getJSON(`Configuration/For/ApprovalWorkflow/In/Customer/${this.idCustomer}`) || {};
			const tmpList = Object.keys(tmpMap).map((pHash) =>
			{
				const tmpBlob = tmpMap[pHash] || {};
				const tmpStates = Array.isArray(tmpBlob.States) ? tmpBlob.States : [];
				let tmpTransitionCount = 0;
				tmpStates.forEach((pState) => { tmpTransitionCount += (Array.isArray(pState.Transitions) ? pState.Transitions.length : 0); });
				return { Hash: pHash, StateCount: tmpStates.length, TransitionCount: tmpTransitionCount };
			});
			tmpList.sort((pA, pB) => String(pA.Hash).localeCompare(String(pB.Hash)));
			tmpState.List = tmpList;
			const tmpLevels = await this._readWorkflowLevelsAsync();
			if (tmpLevels)
			{
				tmpList.forEach((pRow) => { pRow.LevelText = this._listLevelText(tmpLevels.filter((pLevel) => pLevel.NamespaceValue === pRow.Hash)); });
				const tmpListed = new Set(tmpList.map((pRow) => pRow.Hash));
				tmpState.ProjectOnlyList = tmpLevels.filter((pLevel) => (pLevel.LevelContext === 'Project') && !pLevel.Hidden && !tmpListed.has(pLevel.NamespaceValue))
					.map((pLevel) => ({ Hash: pLevel.NamespaceValue, IDProject: pLevel.LevelContextID, ProjectName: pLevel.ProjectName }))
					.sort((pA, pB) => String(pA.Hash).localeCompare(String(pB.Hash)) || (pA.IDProject - pB.IDProject));
			}
		}
		catch (pError)
		{
			tmpState.ListError = `Could not load approval workflows: ${pError && pError.message ? pError.message : pError}`;
			this.pict.log.warn(`ApprovalWorkflow list load failed`, { error: String(pError) });
		}
		tmpState.ListLoading = false;
		this._repaintList();
	}

	/**
	 * Where each of the customer's workflows is set. Best-effort: where it can't be read, nothing is labelled.
	 * @return {Promise<Array<Object>|null>}
	 */
	async _readWorkflowLevelsAsync(pHash)
	{
		try
		{
			const tmpName = pHash ? `/${encodeURIComponent(pHash)}` : '';
			const tmpResult = await this._getJSON(`Configuration/Levels/ApprovalWorkflow${tmpName}/In/Customer/${this.idCustomer}`);
			return (tmpResult && Array.isArray(tmpResult.Levels)) ? tmpResult.Levels : null;
		}
		catch (pError)
		{
			this.pict.log.warn(`ApprovalWorkflow levels read failed`, { error: String(pError) });
			return null;
		}
	}

	/**
	 * @param {Array<Object>} pLevels - Where one workflow is set.
	 * @return {String} What the list should say about that.
	 */
	_listLevelText(pLevels)
	{
		const tmpParts = [];
		const tmpCustomer = pLevels.some((pLevel) => (pLevel.LevelContext === 'Customer') && !pLevel.Hidden);
		if (pLevels.some((pLevel) => pLevel.LevelContext === 'Global'))
		{
			tmpParts.push(tmpCustomer ? 'Customized from the global default' : 'Global default');
		}
		const tmpProjects = pLevels.filter((pLevel) => (pLevel.LevelContext === 'Project') && !pLevel.Hidden).length;
		if (tmpProjects)
		{
			tmpParts.push(`${tmpProjects} project cop${(tmpProjects === 1) ? 'y' : 'ies'}`);
		}
		return tmpParts.join(' · ');
	}

	_repaintList()
	{
		const tmpView = this.pict.views.ApprovalWorkflowList;
		if (tmpView && tmpView.pict)
		{
			tmpView.render();
		}
	}

	// --- Editor load ---

	/**
	 * Load a single workflow blob and resolve the display names its sentences need.
	 * @param {String} pHash  the workflow's Configuration NamespaceValue (a document-type hash, or 'Default')
	 * @param {Number} [pIDProject]  open the copy on this project rather than the customer's
	 */
	async loadWorkflowAsync(pHash, pIDProject)
	{
		const tmpEditor = this.state.Editor;
		const tmpLoadToken = this._beginEditorLoad();
		tmpEditor.Hash = pHash;
		tmpEditor.IsNew = false;
		tmpEditor.IDProject = pIDProject || 0;
		this._repaintEditor();
		try
		{
			const tmpBlob = await this._readWorkflowBlobAsync(pHash);
			if (tmpLoadToken !== this._editorLoadToken)
			{
				return;
			}
			if (!Array.isArray(tmpBlob.States) || !tmpBlob.States.length)
			{
				throw new Error('no workflow is stored under that name for this customer');
			}
			await this._applyWorkflowBlob(tmpBlob);
			if (tmpLoadToken !== this._editorLoadToken)
			{
				return;
			}
			tmpEditor.Editable = this.canEdit;
			this._loadLevelInfoAsync(tmpLoadToken);
		}
		catch (pError)
		{
			if (tmpLoadToken !== this._editorLoadToken)
			{
				return;
			}
			tmpEditor.Workflow = null;
			tmpEditor.Pristine = null;
			tmpEditor.Error = `Could not load workflow "${pHash}", so editing is disabled: ${pError && pError.message ? pError.message : pError}`;
			this.pict.log.warn(`ApprovalWorkflow load failed`, { hash: pHash, error: String(pError) });
		}
		tmpEditor.Loading = false;
		this._repaintEditor();
	}

	/**
	 * Reset the editor for a new load. The token makes a slower earlier load give way, so its blob can't land under a
	 * later workflow's hash, where the next save would overwrite that workflow with it.
	 * @return {Number} this load's token
	 */
	_beginEditorLoad()
	{
		const tmpEditor = this.state.Editor;
		this._editorLoadToken = (this._editorLoadToken || 0) + 1;
		tmpEditor.Loading = true;
		tmpEditor.Error = '';
		tmpEditor.Workflow = null;
		tmpEditor.Pristine = null;
		tmpEditor.Findings = [];
		tmpEditor.DocumentCounts = null;
		tmpEditor.LevelInfo = null;
		tmpEditor.Dirty = false;
		tmpEditor.Editable = false;
		const tmpView = this.pict.views && this.pict.views.ApprovalWorkflowEditor;
		if (tmpView && (typeof tmpView.onWorkflowLoadStarted === 'function'))
		{
			tmpView.onWorkflowLoadStarted();
		}
		return this._editorLoadToken;
	}

	/**
	 * @param {String} pHash
	 * @param {Number} [pIDProject] - Read through this project; defaults to the one being edited.
	 * @return {Promise<Object>} the workflow's blob at that level; {} when none is stored. Rejects if the read fails.
	 */
	async _readWorkflowBlobAsync(pHash, pIDProject)
	{
		const tmpIDProject = (pIDProject === undefined) ? this.state.Editor.IDProject : pIDProject;
		return this._unwrapConfig(await this._postJSON('Configuration/Get',
			{ LevelContext: tmpIDProject ? 'Project' : 'Customer', LevelContextID: tmpIDProject || this.idCustomer, NamespaceKey: 'ApprovalWorkflow', NamespaceValue: pHash }));
	}

	/** The Configuration/Put path for the level being edited. */
	_workflowPutPath(pHash)
	{
		const tmpIDProject = this.state.Editor.IDProject;
		return `Configuration/Put/ApprovalWorkflow/${encodeURIComponent(pHash)}/${tmpIDProject ? `Project/${tmpIDProject}` : `Customer/${this.idCustomer}`}`;
	}

	/**
	 * Work out where the open workflow is set, for the banner. Best-effort: nothing changes if it can't be read.
	 * @param {Number} pLoadToken
	 */
	async _loadLevelInfoAsync(pLoadToken)
	{
		const tmpEditor = this.state.Editor;
		const tmpLevels = await this._readWorkflowLevelsAsync(tmpEditor.Hash);
		if (!tmpLevels || (pLoadToken !== this._editorLoadToken))
		{
			return;
		}
		const tmpVisible = tmpLevels.filter((pLevel) => !pLevel.Hidden);
		const tmpThisProject = tmpVisible.find((pLevel) => (pLevel.LevelContext === 'Project') && (pLevel.LevelContextID === tmpEditor.IDProject));
		tmpEditor.LevelInfo =
		{
			OnGlobal: tmpVisible.some((pLevel) => pLevel.LevelContext === 'Global'),
			OnCustomer: tmpVisible.some((pLevel) => pLevel.LevelContext === 'Customer'),
			Projects: tmpVisible.filter((pLevel) => pLevel.LevelContext === 'Project').map((pLevel) => ({ IDProject: pLevel.LevelContextID, ProjectName: pLevel.ProjectName })),
			ProjectName: tmpThisProject ? tmpThisProject.ProjectName : '',
			OnThisProject: !!tmpThisProject,
		};
		const tmpView = this.pict.views && this.pict.views.ApprovalWorkflowEditor;
		if (tmpView && (typeof tmpView.onLevelInfoLoaded === 'function'))
		{
			tmpView.onLevelInfoLoaded();
		}
	}

	/**
	 * Stop overriding what's inherited: the customer's copy of a global workflow, or a project's copy of anything.
	 * Writing the parent's exact value makes the config service remove this level's row, and writing nothing when there's
	 * no parent removes the row too (api/config Configuration-Put-ByNamespace.js _flattenConfig).
	 * @return {Promise<void>}
	 */
	async revertToInheritedAsync()
	{
		if (!this.canEdit)
		{
			throw new Error('You do not have permission to edit approval workflows.');
		}
		const tmpEditor = this.state.Editor;
		const tmpParent = tmpEditor.IDProject
			? await this._readWorkflowBlobAsync(tmpEditor.Hash, 0)
			: this._unwrapConfig(await this._postJSON('Configuration/Get', { LevelContext: 'Global', NamespaceKey: 'ApprovalWorkflow', NamespaceValue: tmpEditor.Hash }));
		await this._postJSON(this._workflowPutPath(tmpEditor.Hash), { Config: tmpParent });
		tmpEditor.Dirty = false;
	}

	/**
	 * Delete the customer's workflow. Refused while anything still uses it, or while that can't be confirmed, because a
	 * binding left pointing at it stops its documents having a workflow.
	 * @return {Promise<{ Blockers: Array<String>, Deleted: Boolean }>}
	 */
	async deleteWorkflowAsync()
	{
		if (!this.canEdit)
		{
			throw new Error('You do not have permission to edit approval workflows.');
		}
		const tmpEditor = this.state.Editor;
		const tmpBlockers = await this.workflowDeleteBlockersAsync(tmpEditor.Hash);
		if (tmpBlockers.length)
		{
			return { Blockers: tmpBlockers, Deleted: false };
		}
		await this._postJSON(this._workflowPutPath(tmpEditor.Hash), { Config: {} });
		tmpEditor.Dirty = false;
		return { Blockers: [], Deleted: true };
	}

	/**
	 * Everything that stops a workflow being deleted.
	 * @param {String} pHash
	 * @return {Promise<Array<String>>}
	 */
	async workflowDeleteBlockersAsync(pHash)
	{
		const tmpLevels = await this._readWorkflowLevelsAsync(pHash);
		if (!tmpLevels)
		{
			return [ 'Couldn\'t check where this workflow is used.' ];
		}
		const tmpBlockers = [];
		if (tmpLevels.some((pLevel) => pLevel.LevelContext === 'Global'))
		{
			tmpBlockers.push('It comes from the global default. Use revert instead.');
		}
		tmpLevels.filter((pLevel) => (pLevel.LevelContext === 'Project') && !pLevel.Hidden).forEach((pLevel) =>
		{
			tmpBlockers.push(`${pLevel.ProjectName} has its own copy.`);
		});
		try
		{
			const fBoundTypes = (pBindings) =>
			{
				const tmpMap = (pBindings && pBindings.Document) || {};
				return Object.keys(tmpMap).filter((pType) => tmpMap[pType] === pHash);
			};
			fBoundTypes(await this._getJSON(`Configuration/For/ApprovalWorkflowBindings/In/Customer/${this.idCustomer}`)).forEach((pType) =>
			{
				tmpBlockers.push(`${pType} is bound to it.`);
			});
			const tmpBindingLevels = await this._getJSON(`Configuration/Levels/ApprovalWorkflowBindings/Document/In/Customer/${this.idCustomer}`);
			const tmpProjects = ((tmpBindingLevels && tmpBindingLevels.Levels) || []).filter((pLevel) => pLevel.LevelContext === 'Project');
			const tmpQueue = tmpProjects.slice();
			const fWorker = async () =>
			{
				while (tmpQueue.length)
				{
					const tmpProject = tmpQueue.shift();
					const tmpBound = fBoundTypes(await this._getJSON(`Configuration/For/ApprovalWorkflowBindings/In/Project/${tmpProject.LevelContextID}`));
					tmpBound.forEach((pType) =>
					{
						const tmpLine = `${pType} is bound to it on ${tmpProject.ProjectName}.`;
						if (!tmpBlockers.includes(tmpLine) && !tmpBlockers.includes(`${pType} is bound to it.`))
						{
							tmpBlockers.push(tmpLine);
						}
					});
				}
			};
			await Promise.all([ fWorker(), fWorker(), fWorker(), fWorker() ]);
		}
		catch (pError)
		{
			return [ `Couldn't check what's bound to this workflow: ${pError && pError.message ? pError.message : pError}` ];
		}
		return tmpBlockers;
	}

	/**
	 * Seed a brand-new workflow in the editor (unsaved). Its hash is chosen when it is first saved.
	 */
	async newWorkflowAsync()
	{
		const tmpEditor = this.state.Editor;
		this._beginEditorLoad();
		tmpEditor.Hash = '';
		tmpEditor.IDProject = 0;
		tmpEditor.IsNew = true;
		this._repaintEditor();
		await this._applyWorkflowBlob(libModel.NEW_DEFAULT_WORKFLOW_CONFIG);
		tmpEditor.Editable = this.canEdit;
		tmpEditor.Loading = false;
		this._repaintEditor();
	}

	/**
	 * Normalize a blob into the working model, resolve its actor names, and stash it all in the editor state.
	 * @param {Object} pBlob
	 */
	async _applyWorkflowBlob(pBlob)
	{
		const tmpEditor = this.state.Editor;
		const tmpWorkflow = libModel.normalizeLoadedBlob(pBlob);
		tmpEditor.Workflow = tmpWorkflow;
		// Snapshot the just-loaded state so Discard can revert locally (no network round-trip, works even
		// when the last save is the baseline). Refreshed on every successful save.
		tmpEditor.Pristine = JSON.parse(JSON.stringify(tmpWorkflow));
		tmpEditor.Names = await this._resolveActorNamesAsync(tmpWorkflow);
		this._refreshFindings();
	}

	/**
	 * Resolve every role PermissionLevel → Role Name and every referenced IDUser → "First Last" in one pass,
	 * so the (synchronous) transition-node bodies can render their sentences. Best-effort: an unresolved
	 * reference just renders as its raw hash fragment.
	 * @param {Object} pWorkflow
	 * @return {Promise<{ RoleNameByLevel: Object, UserNameByID: Object }>}
	 */
	async _resolveActorNamesAsync(pWorkflow)
	{
		const tmpNames = { RoleNameByLevel: {}, UserNameByID: {}, KnownRoleCodes: null, Users: null };
		const tmpRefs = libModel.collectActorReferences(pWorkflow);
		if (tmpRefs.RoleLevels.length)
		{
			try
			{
				const tmpRoles = await this._getJSON(
					`Roles/FilteredTo/FBV~IDCustomer~EQ~${this.idCustomer}~FBL~PermissionLevel~INN~${tmpRefs.RoleLevels.join(',')}`);
				(Array.isArray(tmpRoles) ? tmpRoles : []).forEach((pRole) =>
				{
					if (pRole && pRole.PermissionLevel != null)
					{
						tmpNames.RoleNameByLevel[String(pRole.PermissionLevel)] = pRole.Name;
					}
				});
				tmpNames.KnownRoleCodes = new Set(Object.keys(tmpNames.RoleNameByLevel));
			}
			catch (pError)
			{
				this.pict.log.warn(`ApprovalWorkflow role-name resolution failed`, { error: String(pError) });
			}
		}
		if (tmpRefs.UserIDs.length)
		{
			try
			{
				const tmpUsers = await this._getJSON(`Users/FilteredTo/FBL~IDUser~INN~${tmpRefs.UserIDs.join(',')}`);
				(Array.isArray(tmpUsers) ? tmpUsers : []).forEach((pUser) =>
				{
					if (pUser && pUser.IDUser != null)
					{
						tmpNames.UserNameByID[String(pUser.IDUser)] = libUserSearch.userLabel(pUser);
						tmpNames.Users = tmpNames.Users || {};
						tmpNames.Users[String(pUser.IDUser)] = { Disabled: pUser.Disabled, IDCustomer: pUser.IDCustomer };
					}
				});
				tmpNames.Users = tmpNames.Users || {};
			}
			catch (pError)
			{
				this.pict.log.warn(`ApprovalWorkflow user-name resolution failed`, { error: String(pError) });
			}
		}
		return tmpNames;
	}

	/**
	 * Bring what's derived from the working model up to date after an edit: names for anything newly referenced, and
	 * the findings.
	 */
	async _afterEditAsync()
	{
		const tmpEditor = this.state.Editor;
		if (!tmpEditor.Workflow)
		{
			return;
		}
		tmpEditor.Names = await this._resolveActorNamesAsync(tmpEditor.Workflow);
		this._refreshFindings();
	}

	/** Re-check the working model. A role or user list that couldn't be read skips its rule rather than reporting everyone. */
	_refreshFindings()
	{
		const tmpEditor = this.state.Editor;
		const tmpNames = tmpEditor.Names || {};
		tmpEditor.Findings = libModel.checkWorkflow(tmpEditor.Workflow,
			{
				KnownRoleCodes: tmpNames.KnownRoleCodes || undefined,
				Users: tmpNames.Users || undefined,
				IDCustomer: this.idCustomer,
				Baseline: tmpEditor.IsNew ? undefined : tmpEditor.Pristine,
			});
	}

	/** What Save would change, by state and action name. */
	pendingChanges()
	{
		const tmpEditor = this.state.Editor;
		return libModel.workflowChanges(tmpEditor.Pristine || {}, tmpEditor.Workflow || {});
	}

	/**
	 * Replace the working model with edited JSON. Refused, leaving the model alone, if it doesn't parse or can't be saved.
	 * @param {String} pText
	 * @return {Promise<{ Error?: String, Changed?: Boolean }>}
	 */
	async applyWorkflowJSONAsync(pText)
	{
		let tmpParsed;
		try
		{
			tmpParsed = JSON.parse(pText);
		}
		catch (pError)
		{
			return { Error: `That isn't valid JSON: ${pError.message}` };
		}
		if (!tmpParsed || (typeof tmpParsed !== 'object') || Array.isArray(tmpParsed))
		{
			return { Error: 'A workflow must be a JSON object with a States list.' };
		}
		const tmpProblems = libModel.findSaveProblems(tmpParsed);
		if (tmpProblems.length)
		{
			return { Error: tmpProblems.join(' ') };
		}
		if (libModel.workflowsEqual(tmpParsed, this.workflow))
		{
			return { Changed: false };
		}
		this.state.Editor.Workflow = libModel.normalizeLoadedBlob(tmpParsed);
		this._markDirty();
		await this._afterEditAsync();
		return { Changed: true };
	}

	/**
	 * Count documents per state, for warning before a rename or delete strands them. On a project's copy this covers that
	 * project. On a customer's workflow it covers document types bound at customer level, so a project-level binding makes
	 * the true number higher. Best-effort: a failed count is left out and blocks nothing.
	 */
	async loadDocumentCountsAsync()
	{
		const tmpEditor = this.state.Editor;
		const tmpToken = this._editorLoadToken;
		if (tmpEditor.DocumentCounts || tmpEditor.IsNew || !tmpEditor.Workflow)
		{
			return;
		}
		tmpEditor.DocumentCounts = {};
		let tmpTypes = [];
		try
		{
			const tmpScope = tmpEditor.IDProject ? `Project/${tmpEditor.IDProject}` : `Customer/${this.idCustomer}`;
			const tmpBindings = await this._getJSON(`Configuration/For/ApprovalWorkflowBindings/In/${tmpScope}`);
			const tmpMap = (tmpBindings && tmpBindings.Document) || {};
			tmpTypes = Object.keys(tmpMap).filter((pType) => (pType !== 'Default') && (tmpMap[pType] === tmpEditor.Hash));
		}
		catch (pError)
		{
			this.pict.log.warn(`ApprovalWorkflow document count: bindings read failed`, { error: String(pError) });
			return;
		}
		if (!tmpTypes.length)
		{
			return;
		}
		const tmpTypeFilter = tmpTypes.map(encodeURIComponent).join(',');
		const tmpScopeFilter = tmpEditor.IDProject ? `FBV~IDProject~EQ~${tmpEditor.IDProject}` : `FBV~IDCustomer~EQ~${this.idCustomer}`;
		const tmpQueue = (tmpEditor.Workflow.States || []).map((pState) => pState.Status);
		const fWorker = async () =>
		{
			while (tmpQueue.length && (tmpToken === this._editorLoadToken))
			{
				const tmpStatus = tmpQueue.shift();
				try
				{
					const tmpResult = await this._getJSON(`Documents/Count/FilteredTo/FBL~DocumentType~INN~${tmpTypeFilter}~${tmpScopeFilter}~FBV~Status~EQ~${encodeURIComponent(tmpStatus)}`);
					if ((tmpToken === this._editorLoadToken) && tmpResult && (typeof tmpResult.Count === 'number'))
					{
						tmpEditor.DocumentCounts[tmpStatus] = tmpResult.Count;
					}
				}
				catch (pError)
				{
					this.pict.log.warn(`ApprovalWorkflow document count failed`, { status: tmpStatus, error: String(pError) });
				}
			}
		};
		await Promise.all([ fWorker(), fWorker(), fWorker(), fWorker() ]);
	}

	/**
	 * @param {String} pStatus
	 * @return {Number} Documents known to be in that state; 0 when none are known.
	 */
	documentCountFor(pStatus)
	{
		const tmpCounts = this.state.Editor.DocumentCounts;
		return (tmpCounts && tmpCounts[pStatus]) || 0;
	}

	/**
	 * Whether a panel's values differ from the transition they edit.
	 * @param {String} pSourceStatus
	 * @param {Number} pTransitionIndex
	 * @param {Object} pPatch
	 * @return {Boolean}
	 */
	transitionPatchChanges(pSourceStatus, pTransitionIndex, pPatch)
	{
		const tmpFound = this._findState(pSourceStatus);
		const tmpTransition = tmpFound && (tmpFound.State.Transitions || [])[pTransitionIndex];
		return !!tmpTransition && libModel.applyTransitionPatch(JSON.parse(JSON.stringify(tmpTransition)), pPatch);
	}

	/**
	 * Whether a panel's values differ from the state they edit.
	 * @param {String} pStatus
	 * @param {{ StateName: String, Status: String }} pPatch
	 * @return {Boolean}
	 */
	statePatchChanges(pStatus, pPatch)
	{
		const tmpFound = this._findState(pStatus);
		return !!tmpFound && ((String(pPatch.Status || '').trim() !== pStatus) || ((tmpFound.State.StateName || '') !== pPatch.StateName));
	}

	// --- Save (P2 uses this; wired now so the data layer is complete) ---

	/**
	 * Persist a working model to the given hash (Customer scope). Refuses anything findSaveProblems rejects, including a
	 * missing or empty model, which would wipe the stored workflow. Returns the advisory findings on success.
	 * @param {String} pHash
	 * @param {Object} [pWorkflow]  the working model (defaults to the editor's current one)
	 * @return {Promise<{ Unreachable: Array<String>, Dangling: Array<String> }>}
	 */
	async saveWorkflowAsync(pHash, pWorkflow)
	{
		if (!this.canEdit)
		{
			throw new Error('You do not have permission to edit approval workflows.');
		}
		const tmpWorkflow = pWorkflow || this.state.Editor.Workflow;
		const tmpProblems = libModel.findSaveProblems(tmpWorkflow);
		if (tmpProblems.length)
		{
			throw new Error(tmpProblems.join(' '));
		}
		const tmpSaveable = libModel.prepareBlobForSave(tmpWorkflow);
		await this._postJSON(this._workflowPutPath(pHash), { Config: tmpSaveable });
		return { Unreachable: libModel.findUnreachableStates(tmpSaveable), Dangling: libModel.findDanglingTargets(tmpSaveable) };
	}

	// --- Clone ---

	/**
	 * @param {String} pName
	 * @return {String} Why that can't be a workflow's name; empty when it can.
	 */
	nameProblem(pName)
	{
		return libModel.workflowNameProblem(pName);
	}

	/** True if the customer already has a workflow stored under pHash (exact match, per the loaded list). */
	workflowHashExists(pHash)
	{
		const tmpHash = String(pHash || '').trim();
		return (this.state.List || []).some((pRow) => String(pRow.Hash) === tmpHash);
	}

	/**
	 * Clone an existing workflow to a new hash (Customer scope). The source's stored blob is copied verbatim:
	 * every intra-workflow reference is a Status string that lives inside the blob, so a deep copy keeps all
	 * wiring valid and there is nothing to remap. Actor references (role PermissionLevels / IDUsers) stay valid
	 * within the same customer. Bindings are untouched — the clone is inert until a document/observation type is
	 * pointed at it on the Bindings screen.
	 *
	 * NOTE: the hash is the Configuration map key, so a Put to an existing hash OVERWRITES it. Callers must
	 * reject a collision first (see workflowHashExists); this method assumes pNewHash is free.
	 * @param {String} pSourceHash  the workflow to copy from
	 * @param {String} pNewHash     the hash/name to store the copy under
	 * @return {Promise<void>}
	 */
	async cloneWorkflowAsync(pSourceHash, pNewHash)
	{
		if (!this.canEdit)
		{
			throw new Error('You do not have permission to clone approval workflows.');
		}
		const tmpResult = await this._postJSON('Configuration/Get',
			{ LevelContext: 'Customer', LevelContextID: this.idCustomer, NamespaceKey: 'ApprovalWorkflow', NamespaceValue: pSourceHash });
		const tmpSourceBlob = this._unwrapConfig(tmpResult);
		if (!tmpSourceBlob || !Array.isArray(tmpSourceBlob.States))
		{
			throw new Error(`Source workflow "${pSourceHash}" could not be read.`);
		}
		// Deep-copy so the persisted clone shares no references with the source, then store it verbatim under the
		// new hash (same direct-blob Put shape as saveWorkflowAsync).
		const tmpClone = JSON.parse(JSON.stringify(tmpSourceBlob));
		await this._postJSON(`Configuration/Put/ApprovalWorkflow/${encodeURIComponent(pNewHash)}/Customer/${this.idCustomer}`, { Config: tmpClone });
	}

	// --- P2 editing: mutate the working blob directly (single source of truth) ---
	//
	// All edits happen on this.state.Editor.Workflow (the normalized blob). The canvas stays read-only; the editor view
	// drives these from its panels and toolbar, then redraws the graph. Branches are read-only for now.

	get workflow()
	{
		return this.state.Editor.Workflow;
	}

	/** The states, as { Status, StateName } — for the target-state pickers. */
	stateOptions()
	{
		return ((this.workflow && this.workflow.States) || []).map((pState) => ({ Status: pState.Status, StateName: pState.StateName }));
	}

	_findState(pStatus)
	{
		const tmpStates = (this.workflow && this.workflow.States) || [];
		const tmpIndex = tmpStates.findIndex((pState) => pState.Status === pStatus);
		return (tmpIndex < 0) ? null : { State: tmpStates[tmpIndex], Index: tmpIndex };
	}

	_markDirty()
	{
		this.state.Editor.Dirty = true;
	}

	/**
	 * Apply an edit to a primary transition. pPatch may set Action, ToStatus, and the four flags; only values that
	 * differ are written, and only a real change marks the editor dirty.
	 * @return {Promise<Boolean>} true if anything changed
	 */
	async applyTransitionEditAsync(pSourceStatus, pTransitionIndex, pPatch)
	{
		const tmpFound = this._findState(pSourceStatus);
		if (!tmpFound)
		{
			return false;
		}
		const tmpTransition = (tmpFound.State.Transitions || [])[pTransitionIndex];
		if (!tmpTransition)
		{
			return false;
		}
		if (!libModel.applyTransitionPatch(tmpTransition, pPatch))
		{
			return false;
		}
		this._markDirty();
		await this._afterEditAsync();
		return true;
	}

	/**
	 * Add a new transition to a state (default action, targeting the first other state). Returns its index.
	 */
	async addTransitionAsync(pSourceStatus)
	{
		const tmpFound = this._findState(pSourceStatus);
		if (!tmpFound) { return -1; }
		if (!Array.isArray(tmpFound.State.Transitions)) { tmpFound.State.Transitions = []; }
		const tmpStates = this.workflow.States;
		const tmpDefaultTarget = (tmpStates.find((pState) => pState.Status !== pSourceStatus) || tmpStates[0]).Status;
		tmpFound.State.Transitions.push({ Action: 'New Action', ToStatus: tmpDefaultTarget, Actors: [] });
		this._markDirty();
		await this._afterEditAsync();
		return tmpFound.State.Transitions.length - 1;
	}

	/** Remove a transition from a state. */
	async removeTransitionAsync(pSourceStatus, pTransitionIndex)
	{
		const tmpFound = this._findState(pSourceStatus);
		if (!tmpFound || !Array.isArray(tmpFound.State.Transitions)) { return; }
		tmpFound.State.Transitions.splice(pTransitionIndex, 1);
		this._markDirty();
		await this._afterEditAsync();
	}

	/**
	 * Edit a state's display name and/or its Status identity. Renaming the Status cascades to every
	 * reference to it. Only a real change marks the editor dirty. Returns the (possibly new) Status plus whether anything
	 * changed, or an { Error }.
	 */
	async applyStateEditAsync(pStatus, pPatch)
	{
		const tmpFound = this._findState(pStatus);
		if (!tmpFound) { return { Error: 'State not found.' }; }
		let tmpChanged = false;
		let tmpNewStatus = pStatus;
		if (pPatch.hasOwnProperty('Status') && (pPatch.Status !== pStatus))
		{
			const tmpProposed = String(pPatch.Status || '').trim();
			if (!tmpProposed) { return { Error: 'State hash cannot be empty.' }; }
			if ((tmpProposed !== pStatus) && this.workflow.States.some((pState) => pState.Status === tmpProposed))
			{
				return { Error: `Another state already uses the hash "${tmpProposed}".` };
			}
			if (tmpProposed !== pStatus)
			{
				libModel.renameStatusCascade(this.workflow, pStatus, tmpProposed);
				tmpFound.State.Status = tmpProposed;
				tmpNewStatus = tmpProposed;
				tmpChanged = true;
			}
		}
		if (pPatch.hasOwnProperty('StateName') && ((tmpFound.State.StateName || '') !== pPatch.StateName))
		{
			tmpFound.State.StateName = pPatch.StateName;
			tmpChanged = true;
		}
		if (tmpChanged)
		{
			this._markDirty();
			await this._afterEditAsync();
		}
		return { Status: tmpNewStatus, Changed: tmpChanged };
	}

	/** Add a new state (unique Status). Returns its Status. */
	async addStateAsync()
	{
		if (!this.workflow.States) { this.workflow.States = []; }
		let tmpN = this.workflow.States.length + 1;
		let tmpStatus = `NewState${tmpN}`;
		while (this.workflow.States.some((pState) => pState.Status === tmpStatus)) { tmpN++; tmpStatus = `NewState${tmpN}`; }
		this.workflow.States.push({ Status: tmpStatus, StateName: 'New State', Transitions: [] });
		this._markDirty();
		await this._afterEditAsync();
		return tmpStatus;
	}

	/** Remove a state and every transition that referenced it (as owner or target). */
	async removeStateAsync(pStatus)
	{
		if (!this.workflow.States) { return; }
		libModel.removeStateCascade(this.workflow, pStatus);
		this._markDirty();
		await this._afterEditAsync();
	}

	// --- P3 actor editing ("who can perform this action") ---
	//
	// Actors stay in their stored form. The editor addresses them through libModel.actorRows, which shows one row per
	// role or user. New role/user actors are objects { Type, Hashes, Tags:[] }; new keyword and level actors are strings
	// ("Admin", "MinimumUserLevelInProject:3").

	/** The customer's roles (cached) for the role pickers: [{ PermissionLevel, Name }] sorted by Name. */
	async loadCustomerRolesAsync()
	{
		if (this._customerRoles)
		{
			return this._customerRoles;
		}
		try
		{
			const tmpRoles = await this._getJSON(`Roles/FilteredTo/FBV~IDCustomer~EQ~${this.idCustomer}`);
			this._customerRoles = (Array.isArray(tmpRoles) ? tmpRoles : [])
				.filter((pRole) => pRole && pRole.PermissionLevel != null)
				.map((pRole) => ({ PermissionLevel: String(pRole.PermissionLevel), Name: pRole.Name || String(pRole.PermissionLevel) }))
				.sort((pA, pB) => pA.Name.localeCompare(pB.Name));
		}
		catch (pError)
		{
			this.pict.log.warn(`ApprovalWorkflow customer-role load failed`, { error: String(pError) });
			this._customerRoles = [];
		}
		return this._customerRoles;
	}

	get customerRoles()
	{
		return this._customerRoles || [];
	}

	/** Best-effort user search for the Users actor picker (the shared user search: name, login ID or email, every
	 *  word must match; the server scopes it to the caller's customer): [{ IDUser, Name }] in name order. */
	async searchUsersAsync(pQuery)
	{
		const tmpQuery = String(pQuery || '').trim();
		if (tmpQuery.length < 2)
		{
			return [];
		}
		try
		{
			const tmpUsers = await libUserSearch.searchUsersAsync((pURL) => this._getJSON(pURL), tmpQuery, { PageSize: 25 });
			return tmpUsers.map((pUser) => ({ IDUser: pUser.IDUser, Name: libUserSearch.userLabel(pUser) }));
		}
		catch (pError)
		{
			this.pict.log.warn(`ApprovalWorkflow user search failed`, { error: String(pError) });
			return [];
		}
	}

	/**
	 * The read-mode detail for a clicked edge (graphviz renderer → { SourceStatus, TransitionIndex, ... }):
	 * the source/target names + the full generated sentence. Sentences resolve role/user names from the
	 * already-loaded Editor.Names.
	 */
	edgeDetail(pInfo)
	{
		const tmpWorkflow = this.workflow;
		if (!tmpWorkflow || !pInfo) { return null; }
		const tmpStateMap = libModel.buildStateMap(tmpWorkflow);
		const tmpState = (tmpWorkflow.States || []).find((pState) => pState.Status === pInfo.SourceStatus);
		if (!tmpState) { return null; }
		const tmpTransition = (tmpState.Transitions || [])[pInfo.TransitionIndex];
		if (!tmpTransition) { return null; }
		return {
			SourceStateName: tmpState.StateName || pInfo.SourceStatus,
			ToStateName: tmpStateMap[pInfo.ToStatus] || pInfo.ToStatus,
			SentenceText: libModel.transitionSentence(tmpTransition, tmpStateMap, this.state.Editor.Names || {}),
			IsBranch: !!pInfo.IsBranch,
		};
	}

	/** Readable rows for a transition's actors (for the editor list): [{ ActorIndex, SettingIndex, Label }]. */
	actorLabels(pSourceStatus, pTransitionIndex)
	{
		const tmpFound = this._findState(pSourceStatus);
		if (!tmpFound) { return []; }
		const tmpTransition = (tmpFound.State.Transitions || [])[pTransitionIndex];
		if (!tmpTransition) { return []; }
		return libModel.actorRows(tmpTransition.Actors, this.state.Editor.Names || { RoleNameByLevel: {}, UserNameByID: {} });
	}

	/** Add an actor (object for role/user, string for keyword/level) to a transition. */
	async addActorAsync(pSourceStatus, pTransitionIndex, pActor)
	{
		const tmpFound = this._findState(pSourceStatus);
		if (!tmpFound) { return; }
		const tmpTransition = (tmpFound.State.Transitions || [])[pTransitionIndex];
		if (!tmpTransition) { return; }
		if (!Array.isArray(tmpTransition.Actors)) { tmpTransition.Actors = []; }
		libModel.addActorSpec(tmpTransition.Actors, pActor);
		this._markDirty();
		await this._afterEditAsync();
	}

	/** Remove one actor row (see libModel.actorRows) from a transition. */
	async removeActorAsync(pSourceStatus, pTransitionIndex, pActorIndex, pSettingIndex)
	{
		const tmpFound = this._findState(pSourceStatus);
		if (!tmpFound) { return; }
		const tmpTransition = (tmpFound.State.Transitions || [])[pTransitionIndex];
		if (!tmpTransition || !Array.isArray(tmpTransition.Actors)) { return; }
		libModel.removeActorRow(tmpTransition.Actors, pActorIndex, pSettingIndex);
		this._markDirty();
		await this._afterEditAsync();
	}

	/** Set the tags on one actor row (see libModel.actorRows) of a transition. */
	async setActorTagsAsync(pSourceStatus, pTransitionIndex, pActorIndex, pSettingIndex, pTags)
	{
		const tmpFound = this._findState(pSourceStatus);
		const tmpTransition = tmpFound && (tmpFound.State.Transitions || [])[pTransitionIndex];
		if (!tmpTransition || !Array.isArray(tmpTransition.Actors))
		{
			return;
		}
		if (libModel.setActorRowTags(tmpTransition.Actors, pActorIndex, pSettingIndex, pTags))
		{
			this._markDirty();
			await this._afterEditAsync();
		}
	}

	/** True while the editor holds edits that have not been saved. */
	hasUnsavedEdits()
	{
		const tmpEditor = this.state.Editor;
		return !!(tmpEditor.Dirty && tmpEditor.Workflow);
	}

	/**
	 * Persist the current working model. Re-reads the stored workflow first and writes nothing if that read fails. A new
	 * workflow may not take a name already in use. An existing one that changed since it was loaded comes back as
	 * { Conflict: true }, unwritten, unless pOptions.Force says to overwrite.
	 * @param {{ Hash?: String, Force?: Boolean }} [pOptions] - Hash names a new workflow.
	 * @return {Promise<{ Conflict?: Boolean, Changes?: Array<String>, Unreachable?: Array<String>, Dangling?: Array<String> }>}
	 */
	async saveAsync(pOptions)
	{
		const tmpOptions = pOptions || {};
		const tmpEditor = this.state.Editor;
		const tmpHash = tmpOptions.Hash || tmpEditor.Hash;
		const tmpNameProblem = libModel.workflowNameProblem(tmpHash);
		if (tmpNameProblem)
		{
			throw new Error(tmpNameProblem);
		}
		const tmpProblems = libModel.findSaveProblems(tmpEditor.Workflow);
		if (tmpProblems.length)
		{
			throw new Error(tmpProblems.join(' '));
		}
		let tmpLive;
		try
		{
			tmpLive = await this._readWorkflowBlobAsync(tmpHash);
		}
		catch (pError)
		{
			throw new Error(`Nothing was saved: the stored workflow could not be checked (${pError && pError.message ? pError.message : pError}).`);
		}
		if (tmpEditor.IsNew)
		{
			if (Object.keys(tmpLive).length)
			{
				throw new Error(`A workflow named "${tmpHash}" already exists. Choose a different name.`);
			}
		}
		else if (!tmpOptions.Force && !libModel.workflowsEqual(libModel.normalizeLoadedBlob(tmpLive), tmpEditor.Pristine))
		{
			return { Conflict: true, Changes: libModel.workflowChanges(tmpEditor.Pristine || {}, libModel.normalizeLoadedBlob(tmpLive)) };
		}
		const tmpResult = await this.saveWorkflowAsync(tmpHash, tmpEditor.Workflow);
		tmpEditor.Hash = tmpHash;
		tmpEditor.IsNew = false;
		tmpEditor.Dirty = false;
		// The saved state is the new baseline for Discard and for the next save's check.
		tmpEditor.Pristine = JSON.parse(JSON.stringify(tmpEditor.Workflow));
		this._refreshFindings();
		return tmpResult;
	}

	// --- P4 bindings: which approval workflow governs each document type ---------------------------------
	//
	// Stored at Configuration namespace 'ApprovalWorkflowBindings', NamespaceValue 'Document', as a flat map
	// { "<documentTypeHash>": "<workflowHash>" }. Document types are ReportNamedInstance rows; a synthetic
	// "Default" row is the fallback for any document without its own binding.
	//
	// SCOPE. Bindings cascade Global -> Customer -> Project (deeper wins, PER KEY — see the config service).
	// This screen edits two of those levels, mirroring Home:
	//   - Customer scope: the customer-wide default set (what every project inherits).
	//   - Project scope:  one project's overrides. A project's EFFECTIVE map (what the approvals engine reads)
	//     is the customer map with the project's own keys layered on top; the read endpoint already returns
	//     that merge, so tmpState.Map is always the effective map for the active scope and tmpState.CustomerMap
	//     is the default reference used to tell inherited rows from overrides. On save we write the whole
	//     effective map back to the scope; the server keeps only genuine overrides (a value equal to the parent
	//     is dropped -> inherit; a parent key omitted is hidden -> opt-out), so project bindings can diverge
	//     from the customer default without duplicating the whole set.

	// Project-scope dropdown sentinels (not real workflow hashes): "use the customer default" and "no workflow".
	get BINDING_INHERIT() { return BINDING_INHERIT; }
	get BINDING_NONE() { return BINDING_NONE; }

	get bindingsState()
	{
		if (!this.state.Bindings)
		{
			this.state.Bindings = { Loading: false, Error: '', Rows: [], WorkflowHashes: [], Map: {}, Dirty: false, Editable: false,
				EntityType: 'Document', Title: BINDING_FAMILIES.Document.Title, TargetNoun: BINDING_FAMILIES.Document.TargetNoun, TargetNounPlural: BINDING_FAMILIES.Document.TargetNounPlural, ServerPending: false,
				// Scope: 'Customer' (the default set) or 'Project' (one project's overrides).
				Scope: 'Customer', IDProject: 0, ProjectName: '', ScopeLoading: false,
				CustomerMap: {}, CustomerMapLoaded: false, LoadedScopeKey: '', Targets: [], Projects: [], ProjectsLoaded: false };
		}
		return this.state.Bindings;
	}

	/** The (Scope, ScopeID) the bindings map is read from / written to right now. */
	_bindingScope()
	{
		const tmpState = this.bindingsState;
		return (tmpState.Scope === 'Project' && tmpState.IDProject > 0)
			? { Scope: 'Project', ScopeID: tmpState.IDProject }
			: { Scope: 'Customer', ScopeID: this.idCustomer };
	}

	/**
	 * Fetch the effective bindings map ({ typeHash: workflowHash }) for the entity type at a scope. Rejects when the
	 * read fails: the map is written back whole on save, so treating a failed read as an empty map would wipe the
	 * scope's bindings (or opt a project out of every customer binding) on the next save.
	 */
	async _fetchBindingsMap(pScope, pScopeID, pEntityType)
	{
		const tmpBindings = await this._getJSON(`Configuration/For/ApprovalWorkflowBindings/In/${pScope}/${pScopeID}`);
		return Object.assign({}, (tmpBindings && tmpBindings[pEntityType]) || {});
	}

	/** Key of the scope whose map is in hand, e.g. 'Customer:10' or 'Project:34876'. */
	_bindingScopeKey(pScope)
	{
		return `${pScope.Scope}:${pScope.ScopeID}`;
	}

	/**
	 * Why the active scope's bindings can't be saved right now ('' when they can). Saving is refused unless the map
	 * on screen came from a successful read of the scope being written.
	 * @return {String}
	 */
	bindingsSaveBlockedReason()
	{
		const tmpState = this.bindingsState;
		if (tmpState.Loading || tmpState.ScopeLoading)
		{
			return 'The bindings are still loading.';
		}
		if (tmpState.LoadedScopeKey !== this._bindingScopeKey(this._bindingScope()))
		{
			return 'The current bindings could not be read, so saving is disabled: a save would overwrite them. Reload the page and try again.';
		}
		return '';
	}

	/**
	 * Load the target types (documents or observations) + workflow hashes + the current bindings map into the
	 * bindings state, for the given family. Workflows are the SHARED pool; only the target entity and the
	 * bindings NamespaceValue differ. Always resets to Customer scope. @param {String} [pEntityType] 'Document'
	 * (default) or 'Observation'.
	 */
	async loadBindingsAsync(pEntityType)
	{
		const tmpFamily = BINDING_FAMILIES[pEntityType] || BINDING_FAMILIES.Document;
		const tmpState = this.bindingsState;
		tmpState.EntityType = tmpFamily.EntityType;
		tmpState.Title = tmpFamily.Title;
		tmpState.TargetNoun = tmpFamily.TargetNoun;
		tmpState.TargetNounPlural = tmpFamily.TargetNounPlural;
		tmpState.ServerPending = tmpFamily.ServerPending;
		tmpState.Loading = true;
		tmpState.Error = '';
		tmpState.Dirty = false;
		tmpState.Editable = this.canEdit;
		// A fresh page load always starts on the customer default set.
		tmpState.Scope = 'Customer';
		tmpState.IDProject = 0;
		tmpState.ProjectName = '';
		tmpState.CustomerMapLoaded = false;
		tmpState.LoadedScopeKey = '';
		this._repaintBindings();
		try
		{
			// No fallback to {}: without the workflow list every row's dropdown would be empty (see _fetchBindingsMap).
			const tmpWorkflowMap = await this._getJSON(`Configuration/For/ApprovalWorkflow/In/Customer/${this.idCustomer}`);
			const tmpTargets = await this._getJSON(tmpFamily.targetsURL(this.idCustomer)).catch(() => []);

			tmpState.CustomerWorkflowHashes = Object.keys(tmpWorkflowMap || {}).sort((pA, pB) => String(pA).localeCompare(String(pB)));
			tmpState.WorkflowHashes = tmpState.CustomerWorkflowHashes;
			tmpState.Targets = (Array.isArray(tmpTargets) ? tmpTargets : [])
				.map((pRow) => ({ Key: tmpFamily.targetKey(pRow), Label: tmpFamily.targetLabel(pRow) }))
				.filter((pRow) => pRow.Key);
			// The customer map is both the customer-scope working map and the default reference for project scope.
			tmpState.CustomerMap = await this._fetchBindingsMap('Customer', this.idCustomer, tmpFamily.EntityType);
			tmpState.CustomerMapLoaded = true;
			tmpState.LoadedScopeKey = this._bindingScopeKey(this._bindingScope());
			tmpState.Map = Object.assign({}, tmpState.CustomerMap);
			this._rebuildBindingRows();
		}
		catch (pError)
		{
			tmpState.Rows = [];
			tmpState.Map = {};
			tmpState.Error = `Could not load bindings, so editing is disabled: ${pError && pError.message ? pError.message : pError}`;
			this.pict.log.warn(`ApprovalWorkflow bindings load failed`, { error: String(pError), entityType: tmpFamily.EntityType });
		}
		tmpState.Loading = false;
		this._repaintBindings();
		// The project scope picker is server-side search now (mountBindingProjectPicker) — no capped bulk preload
		// (the old `Projects/Lite/…/0/1000` made projects past 1000 permanently unselectable for an override).
	}

	/**
	 * (Re)mount the searchable project-scope picker into #ApprovalWorkflowBindings-ProjectPicker. Server-side search
	 * over the customer's projects (replaces the capped `Projects/Lite/…/0/1000` <select>). Called from the bindings
	 * view's onAfterRender, so it re-creates into the fresh mount each repaint; the selection is preserved via the
	 * ValueAddress binding on Bindings.IDProject + ResolveValue. Mirrors the RoleClone / ProjectEditWorkspace pattern.
	 */
	mountBindingProjectPicker()
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement('#ApprovalWorkflowBindings-ProjectPicker').length === 0) { return; }
		const tmpView = tmpPicker.createPicker('ApprovalWorkflowBindings-ProjectPicker',
			{
				DestinationAddress: '#ApprovalWorkflowBindings-ProjectPicker',
				ValueAddress: 'AppData.ApprovalWorkflowEditor.Bindings.IDProject',
				Searchable: true, AllowClear: false,
				// The bound value is Bindings.IDProject, whose "no project chosen" state is 0. Tell the picker to
				// paint 0 as the placeholder instead of a literal "0" selection (it still stores/returns 0).
				EmptyValues: [ 0 ],
				Placeholder: 'Select a project…',
				DataProvider: (pTerm, pPage) =>
				{
					const tmpStart = (Number(pPage) || 0) * 25;
					let tmpFilter = `FBV~IDCustomer~EQ~${this.idCustomer}~FBV~Deleted~EQ~0`;
					const tmpTerm = String(pTerm == null ? '' : pTerm).trim();
					if (tmpTerm) { tmpFilter = `FBV~Name~LK~${encodeURIComponent(`%${tmpTerm}%`)}~${tmpFilter}`; }
					return this._getJSON(`Projects/Lite/FilteredTo/${tmpFilter}/${tmpStart}/25`)
						// The Lite projection returns the project NAME under `Value` (not `Name`), so read that first —
						// otherwise the browse list shows "Project <id>" for every row.
						.then((pRows) => ({ results: (Array.isArray(pRows) ? pRows : []).map((pP) => ({ Value: Number(pP.IDProject), Text: pP.Value || pP.Name || `Project ${pP.IDProject}`, Record: pP })), hasMore: (Array.isArray(pRows) && (pRows.length >= 25)) }))
						.catch(() => ({ results: [], hasMore: false }));
				},
				ResolveValue: (pValue) =>
				{
					const tmpID = Number(pValue);
					if (!tmpID) { return Promise.resolve(null); }
					return this._getJSON(`Project/${tmpID}`)
						.then((pP) => ((pP && pP.IDProject) ? { Value: Number(pP.IDProject), Text: pP.Name || `Project ${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` }))
						.catch(() => ({ Value: tmpID, Text: `#${tmpID}` }));
				},
				OnChange: (pValue) => { const tmpBindingsView = this.pict.views.ApprovalWorkflowBindings; if (tmpBindingsView) { tmpBindingsView.onProjectChange(pValue || 0); } },
			});
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
	}

	/**
	 * Switch the bindings screen between the customer default set and one project's overrides. Loads the target
	 * scope's effective map (customer map is already cached). @param {String} pScope 'Customer'|'Project'.
	 */
	async switchBindingScopeAsync(pScope, pIDProject)
	{
		const tmpState = this.bindingsState;
		const tmpScope = (pScope === 'Project') ? 'Project' : 'Customer';
		const tmpIDProject = parseInt(pIDProject, 10) || 0;
		tmpState.Scope = tmpScope;
		tmpState.Dirty = false;
		tmpState.Error = '';
		if (tmpScope === 'Project' && tmpIDProject > 0)
		{
			const tmpProject = (tmpState.Projects || []).find((pProject) => Number(pProject.IDProject) === tmpIDProject);
			tmpState.IDProject = tmpIDProject;
			// The picker isn't a preloaded list, so the chosen project may not be cached — resolve its name by id.
			if (tmpProject) { tmpState.ProjectName = tmpProject.Name; }
			else { const tmpFetched = await this._getJSON(`Project/${tmpIDProject}`).catch(() => null); tmpState.ProjectName = (tmpFetched && tmpFetched.Name) || `Project ${tmpIDProject}`; }
			tmpState.ScopeLoading = true;
			tmpState.LoadedScopeKey = '';
			this._repaintBindings();
			try
			{
				tmpState.Map = await this._fetchBindingsMap('Project', tmpIDProject, tmpState.EntityType);
				// A project can bind to a workflow set only on itself, which the customer's list doesn't include.
				const tmpProjectWorkflows = await this._getJSON(`Configuration/For/ApprovalWorkflow/In/Project/${tmpIDProject}`);
				tmpState.WorkflowHashes = Array.from(new Set((tmpState.CustomerWorkflowHashes || []).concat(Object.keys(tmpProjectWorkflows || {})))).sort((pA, pB) => String(pA).localeCompare(String(pB)));
				tmpState.LoadedScopeKey = this._bindingScopeKey(this._bindingScope());
				this._rebuildBindingRows();
			}
			catch (pError)
			{
				tmpState.Map = {};
				tmpState.Rows = [];
				tmpState.Error = `Could not load ${tmpState.ProjectName}'s bindings, so editing is disabled: ${pError && pError.message ? pError.message : pError}`;
			}
			tmpState.ScopeLoading = false;
		}
		else if (tmpScope === 'Project')
		{
			// Project scope selected but no project chosen yet — show the prompt, wait for a pick.
			tmpState.IDProject = 0;
			tmpState.ProjectName = '';
			tmpState.Map = {};
			tmpState.Rows = [];
			tmpState.LoadedScopeKey = '';
		}
		else
		{
			tmpState.IDProject = 0;
			tmpState.ProjectName = '';
			tmpState.WorkflowHashes = tmpState.CustomerWorkflowHashes || [];
			tmpState.Map = Object.assign({}, tmpState.CustomerMap);
			tmpState.LoadedScopeKey = tmpState.CustomerMapLoaded ? this._bindingScopeKey(this._bindingScope()) : '';
			if (!tmpState.CustomerMapLoaded)
			{
				tmpState.Error = 'The customer default bindings could not be read, so editing is disabled. Reload the page and try again.';
			}
			this._rebuildBindingRows();
		}
		this._repaintBindings();
	}

	/** Rebuild the target-row list (synthetic Default + every target type + any bound key not in the list). */
	_rebuildBindingRows()
	{
		const tmpState = this.bindingsState;
		const tmpRows = [ { DocHash: 'Default', DocName: 'Default', Synthetic: true } ];
		const tmpSeen = { Default: true };
		(tmpState.Targets || []).forEach((pTarget) =>
		{
			if (pTarget.Key && !tmpSeen[pTarget.Key])
			{
				tmpSeen[pTarget.Key] = true;
				tmpRows.push({ DocHash: pTarget.Key, DocName: pTarget.Label || pTarget.Key });
			}
		});
		// Orphans = bound keys (in this scope's map OR the customer default) with no matching target type.
		Object.keys(tmpState.Map || {}).concat(Object.keys(tmpState.CustomerMap || {})).forEach((pKey) =>
		{
			if (!tmpSeen[pKey]) { tmpSeen[pKey] = true; tmpRows.push({ DocHash: pKey, DocName: pKey, Orphan: true }); }
		});
		tmpState.Rows = tmpRows;
	}

	/**
	 * Set the binding for a type in the active scope. In project scope the value may be a sentinel: BINDING_INHERIT
	 * copies the customer default (server dedupes it back to "inherit"); BINDING_NONE / '' clears it (in project
	 * scope an omitted parent key is hidden -> the project opts out of the default). Marks dirty.
	 */
	setBinding(pDocHash, pValue)
	{
		const tmpState = this.bindingsState;
		if (pValue === BINDING_INHERIT)
		{
			const tmpDefault = tmpState.CustomerMap[pDocHash];
			if (tmpDefault) { tmpState.Map[pDocHash] = tmpDefault; }
			else { delete tmpState.Map[pDocHash]; }
		}
		else if (pValue && pValue !== BINDING_NONE)
		{
			tmpState.Map[pDocHash] = pValue;
		}
		else
		{
			delete tmpState.Map[pDocHash];
		}
		tmpState.Dirty = true;
	}

	/**
	 * Persist the active scope's bindings map. Writes the whole effective map to Customer or Project scope; the
	 * server keeps only genuine overrides. Returns { Count, Scope, OverrideCount }. On a project save the map is
	 * re-fetched so the inherited/override badges reflect what was actually stored.
	 */
	async saveBindingsAsync()
	{
		if (!this.canEdit)
		{
			throw new Error('You do not have permission to edit approval-workflow bindings.');
		}
		const tmpBlockedReason = this.bindingsSaveBlockedReason();
		if (tmpBlockedReason)
		{
			throw new Error(tmpBlockedReason);
		}
		const tmpState = this.bindingsState;
		const tmpEntityType = tmpState.EntityType || 'Document';
		const tmpScope = this._bindingScope();
		const tmpMap = {};
		Object.keys(tmpState.Map).forEach((pHash) => { if (tmpState.Map[pHash]) { tmpMap[pHash] = tmpState.Map[pHash]; } });
		await this._postJSON(`Configuration/Put/ApprovalWorkflowBindings/${tmpEntityType}/${tmpScope.Scope}/${tmpScope.ScopeID}`, tmpMap);
		tmpState.Dirty = false;
		const tmpOverrideCount = this.bindingOverrideCount();
		if (tmpScope.Scope === 'Project')
		{
			// Reflect the server's dedup/hide: re-read the effective map so inherited vs override reads true. The save
			// itself succeeded; if the re-read fails, block further edits until a reload rather than show a stale map.
			try
			{
				tmpState.Map = await this._fetchBindingsMap('Project', tmpScope.ScopeID, tmpEntityType);
			}
			catch (pError)
			{
				tmpState.LoadedScopeKey = '';
				tmpState.Rows = [];
				tmpState.Error = `Saved, but the bindings could not be read back, so editing is disabled. Reload the page before editing again: ${pError && pError.message ? pError.message : pError}`;
			}
			if (!tmpState.Error)
			{
				this._rebuildBindingRows();
			}
			this._repaintBindings();
		}
		else
		{
			// Keep the default reference fresh so project scope compares against what was just saved.
			tmpState.CustomerMap = Object.assign({}, tmpMap);
		}
		return { Count: Object.keys(tmpMap).length, Scope: tmpScope.Scope, OverrideCount: tmpOverrideCount };
	}

	/** How many types in the active (project) scope diverge from the customer default. 0 for customer scope. */
	bindingOverrideCount()
	{
		const tmpState = this.bindingsState;
		if (tmpState.Scope !== 'Project') { return 0; }
		const tmpKeys = {};
		Object.keys(tmpState.Map || {}).concat(Object.keys(tmpState.CustomerMap || {})).forEach((pKey) => { tmpKeys[pKey] = true; });
		let tmpCount = 0;
		Object.keys(tmpKeys).forEach((pKey) =>
		{
			if ((tmpState.Map[pKey] || '') !== (tmpState.CustomerMap[pKey] || '')) { tmpCount++; }
		});
		return tmpCount;
	}

	_repaintBindings()
	{
		const tmpView = this.pict.views.ApprovalWorkflowBindings;
		if (tmpView && tmpView.pict)
		{
			tmpView.render();
		}
	}

	/** Revert edits to the last-loaded/saved state. Restores the in-memory snapshot (no network); falls back
	 * to a server reload / default re-seed only if no snapshot exists. */
	async discardAsync()
	{
		const tmpEditor = this.state.Editor;
		if (tmpEditor.Pristine)
		{
			tmpEditor.Workflow = JSON.parse(JSON.stringify(tmpEditor.Pristine));
			tmpEditor.Dirty = false;
			this._refreshFindings();
			tmpEditor.Error = '';
			this._repaintEditor();
			return;
		}
		if (tmpEditor.IsNew)
		{
			await this.newWorkflowAsync();
		}
		else
		{
			await this.loadWorkflowAsync(tmpEditor.Hash, tmpEditor.IDProject);
		}
		tmpEditor.Dirty = false;
	}

	_repaintEditor()
	{
		const tmpView = this.pict.views.ApprovalWorkflowEditor;
		if (tmpView && tmpView.pict)
		{
			tmpView.render();
		}
	}
}

module.exports = ApprovalWorkflowProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'ApprovalWorkflow',
	AutoInitialize: true,
	AutoInitializeOrdinal: 30,
};
